import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { UserStats } from '../components/settings/StatsSection';

// Removed StudySession interface since we're not tracking study time yet

interface UserProgressData {
  flashcardProgress: any[];
  textbookProgress: any[];
  audiobookProgress: any[];
  exerciseProgress: any[];
}

export const getUserStatistics = async (uid: string): Promise<UserStats> => {
  console.log('🔍 Fetching statistics for user:', uid);
  
  let gamificationData = null;
  // Removed study sessions since we're not tracking study time yet
  let progressData: UserProgressData = {
    flashcardProgress: [],
    textbookProgress: [],
    audiobookProgress: [],
    exerciseProgress: []
  };

  try {
    // Get user document which contains gamification data
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      gamificationData = userData?.gamification || null;
      console.log('✅ Gamification data loaded:', gamificationData);
    } else {
      console.log('❌ User document does not exist');
    }
  } catch (error) {
    console.error('❌ Error fetching user document:', error);
  }

  // Removed study sessions fetching since we're not tracking study time yet

  try {
    // Get user's progress data
    progressData = await getUserProgressData(uid);
    console.log('✅ Progress data loaded:', progressData);
  } catch (error) {
    console.error('❌ Error fetching progress data (using empty data):', error);
  }

  // Calculate statistics with whatever data we have
  const stats = calculateStatistics(gamificationData, progressData);
  console.log('📊 Final calculated stats:', stats);

  return stats;
};

const getUserProgressData = async (uid: string): Promise<UserProgressData> => {
  const progressData: UserProgressData = {
    flashcardProgress: [],
    textbookProgress: [],
    audiobookProgress: [],
    exerciseProgress: []
  };

  // Try to get flashcard progress
  try {
    const flashcardProgressQuery = query(
      collection(db, 'flashcardProgress'),
      where('userId', '==', uid)
    );
    const flashcardProgressSnapshot = await getDocs(flashcardProgressQuery);
    progressData.flashcardProgress = flashcardProgressSnapshot.docs.map(doc => doc.data());
    console.log('✅ Flashcard progress loaded:', progressData.flashcardProgress.length);
  } catch (error) {
    console.error('❌ Error fetching flashcard progress:', error);
  }

  // Try to get textbook progress
  try {
    const textbookProgressQuery = query(
      collection(db, 'textbookProgress'),
      where('userId', '==', uid)
    );
    const textbookProgressSnapshot = await getDocs(textbookProgressQuery);
    progressData.textbookProgress = textbookProgressSnapshot.docs.map(doc => doc.data());
    console.log('✅ Textbook progress loaded:', progressData.textbookProgress.length);
  } catch (error) {
    console.error('❌ Error fetching textbook progress:', error);
  }

  // Try to get audiobook progress
  try {
    const audiobookProgressQuery = query(
      collection(db, 'audiobookProgress'),
      where('userId', '==', uid)
    );
    const audiobookProgressSnapshot = await getDocs(audiobookProgressQuery);
    progressData.audiobookProgress = audiobookProgressSnapshot.docs.map(doc => doc.data());
    console.log('✅ Audiobook progress loaded:', progressData.audiobookProgress.length);
  } catch (error) {
    console.error('❌ Error fetching audiobook progress:', error);
  }

  return progressData;
};

const calculateStatistics = (
  gamificationData: any,
  progressData: UserProgressData
): UserStats => {
  console.log('📊 Calculating statistics with data:', {
    hasGamificationData: !!gamificationData,
    gamificationStreak: gamificationData?.streak,
    progressCounts: {
      flashcard: progressData.flashcardProgress.length,
      textbook: progressData.textbookProgress.length,
      audiobook: progressData.audiobookProgress.length
    }
  });

  // Calculate completed modules from progress data
  const completedModules = 
    progressData.flashcardProgress.filter(p => p.isCompleted).length +
    progressData.textbookProgress.filter(p => p.isCompleted).length +
    progressData.audiobookProgress.filter(p => p.isCompleted).length;

  // Get streak data from gamification - use the actual Firebase data
  const currentStreak = gamificationData?.streak?.current ?? 0;
  const longestStreak = gamificationData?.streak?.longest ?? 0;

  console.log('🔥 Streak data from Firebase:', { currentStreak, longestStreak });

  // Create study history based on gamification data
  const studyHistory = [];
  if (gamificationData?.streak?.lastActivityDate) {
    const lastActivityDate = gamificationData.streak.lastActivityDate;
    studyHistory.push({
      date: lastActivityDate,
      minutes: 30 // Default session time for display purposes
    });
  }

  const result = {
    totalStudyTime: 0, // Not tracking study time yet
    completedModules,
    currentStreak,
    longestStreak,
    averageSessionTime: 0, // Not tracking session time yet
    studyHistory
  };

  console.log('📈 Final calculated statistics:', result);
  return result;
};

// Cache for statistics to avoid frequent Firestore calls
const statsCache = new Map<string, { data: UserStats; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getCachedUserStatistics = async (uid: string): Promise<UserStats> => {
  const cached = statsCache.get(uid);
  const now = Date.now();

  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }

  const stats = await getUserStatistics(uid);
  statsCache.set(uid, { data: stats, timestamp: now });
  
  return stats;
};

// Function to invalidate cache when user completes activities
export const invalidateStatsCache = (uid: string): void => {
  statsCache.delete(uid);
};

// Function to force refresh statistics (bypasses cache)
export const refreshUserStatistics = async (uid: string): Promise<UserStats> => {
  // Clear cache first
  statsCache.delete(uid);
  // Fetch fresh data
  return await getUserStatistics(uid);
};

// Simple test function to verify Firebase connection and data
export const testFirebaseConnection = async (uid: string): Promise<any> => {
  try {
    console.log('🧪 Testing Firebase connection for user:', uid);
    
    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      console.log('✅ User document exists:', userData);
      return userData;
    } else {
      console.log('❌ User document does not exist');
      return null;
    }
  } catch (error) {
    console.error('❌ Firebase connection test failed:', error);
    throw error;
  }
};

// Mock data generator for development/testing (fallback only)
export const generateMockStats = (): UserStats => {
  const now = new Date();
  const studyHistory = [];
  
  // Generate simple study history for display
  for (let i = 0; i < 5; i++) {
    const date = new Date(now.getTime() - (i * 24 * 60 * 60 * 1000));
    studyHistory.push({
      date: date.toISOString(),
      minutes: 30 // Default display value
    });
  }

  const currentStreak = Math.floor(Math.random() * 10) + 1; // 1-10 days
  const longestStreak = Math.max(currentStreak, Math.floor(Math.random() * 20) + 5); // At least current streak

  return {
    totalStudyTime: 0, // Not tracking yet
    completedModules: Math.floor(Math.random() * 10) + 2, // 2-12 modules
    currentStreak,
    longestStreak,
    averageSessionTime: 0, // Not tracking yet
    studyHistory
  };
};